import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { SearchEmpty } from "@/components/ui/EmptyState";
import { Field, FieldReset } from "@/components/ui/Field";
import { RadioIndicator } from "@/components/ui/Indicators";
import { Scrim } from "@/components/ui/Scrim";
import { SelectTrigger } from "@/components/ui/Select";
import { SearchField } from "@/components/ui/SearchField";
import { SheetBody } from "@/components/ui/Sheet";
import { Text } from "@/components/ui/Typography";
import { useModalLayer } from "@/contexts/ModalContext";
import { useLockBodyScroll } from "@/hooks/use-lock-body-scroll";
import { useRovingFocus, useRovingTabStop } from "@/hooks/use-roving-focus";
import { Back } from "@/icons";
import { dialogVariants, TRANSITION } from "@/lib/motion";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useMemo, useState, type KeyboardEvent } from "react";
import { Surface } from "@/components/ui/Surface";

const MotionSurface = motion.create(Surface);

const MotionScrim = motion.create(Scrim);

export type LocationPickerOption = {
  id: string;
  name: string;
};

/**
 * Closed-list picker for a location field (city or neighborhood): a Select-looking trigger opens a modal
 * dialog, full screen on phones, with a back arrow and a search that only takes focus when tapped.
 * Touching the list dismisses the keyboard, so no option stays hidden behind it.
 */
export function LocationPickerField({
  name,
  label,
  required = true,
  disabled = false,
  error = false,
  errorMessage,
  dialogTitle,
  searchPlaceholder,
  emptyMessage,
  triggerPlaceholder,
  selectedName,
  options,
  onSelect,
}: {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  dialogTitle: string;
  searchPlaceholder: string;
  emptyMessage: string;
  triggerPlaceholder: string;
  selectedName: string;
  options: LocationPickerOption[];
  onSelect: (option: LocationPickerOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const reduceMotion = useReducedMotion();
  const { zIndex, backdropZIndex } = useModalLayer(open);
  useLockBodyScroll(open);
  const roveOptions = useRovingFocus<HTMLButtonElement>({ selector: '[role="radio"]:not(:disabled)' });
  const optionsRef = useRovingTabStop<HTMLDivElement>();

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return options;
    return options.filter((option) => option.name.toLowerCase().includes(query));
  }, [options, search]);

  function changeOpen(next: boolean) {
    setOpen(next);
    if (!next) setSearch("");
  }

  function selectOption(option: LocationPickerOption) {
    onSelect(option);
    changeOpen(false);
  }

  function dismissKeyboard() {
    if (document.activeElement instanceof HTMLInputElement) document.activeElement.blur();
  }

  function blurOnEnter(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") event.currentTarget.blur();
  }

  return (
    <Field id={name + label} label={label} required={required} invalid={error} error={error ? errorMessage : undefined}>
      <Dialog.Root modal open={open} onOpenChange={changeOpen}>
        <Dialog.Trigger asChild>
          <SelectTrigger disabled={disabled} value={selectedName} placeholder={triggerPlaceholder} />
        </Dialog.Trigger>
        <FieldReset>
          <AnimatePresence>
            {open && (
              <Dialog.Portal forceMount>
                <Dialog.Overlay forceMount asChild>
                  <MotionScrim
                    zIndex={backdropZIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={TRANSITION.fade}
                  />
                </Dialog.Overlay>
                <Dialog.Content forceMount asChild aria-modal="true" aria-describedby={undefined} style={{ zIndex }}>
                  <MotionSurface
                    surface="card"
                    variants={dialogVariants(reduceMotion)}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="fixed inset-0 flex h-full w-full flex-col md:inset-x-0 md:inset-y-3 md:m-auto md:h-fit md:max-h-sheet md:w-11/12 md:max-w-md md:rounded-sheet md:shadow-overlay"
                  >
                    <Dialog.Title className="sr-only">{dialogTitle}</Dialog.Title>
                    <div className="flex items-center gap-2 border-b border-border px-3 pb-3 pt-[max(env(safe-area-inset-top),0.75rem)] md:pt-3">
                      <Dialog.Close asChild>
                        <IconButton icon={Back} label="Voltar" tone="primary" size="icon-bar" />
                      </Dialog.Close>
                      <SearchField
                        className="min-w-0 flex-1"
                        placeholder={searchPlaceholder}
                        aria-label={searchPlaceholder}
                        enterKeyHint="done"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        onClear={() => setSearch("")}
                        onKeyDown={blurOnEnter}
                      />
                    </div>
                    <SheetBody className="px-2 pb-safe md:px-2 md:pb-3" onTouchMove={dismissKeyboard}>
                      <div
                        ref={optionsRef}
                        role="radiogroup"
                        aria-label={dialogTitle}
                        onKeyDown={roveOptions}
                        className="divide-y divide-border"
                      >
                        {filteredOptions.map((option) => {
                          const selected = selectedName === option.name;
                          return (
                            <Button
                              key={option.id}
                              onClick={() => selectOption(option)}
                              role="radio"
                              aria-checked={selected}
                              variant="ghost"
                              tone="neutral"
                              size="row"
                              shape="square"
                              align="between"
                              fullWidth
                            >
                              <span>{option.name}</span>
                              <RadioIndicator checked={selected} size="sm" />
                            </Button>
                          );
                        })}
                      </div>
                      {options.length === 0 ? (
                        <div className="py-6">
                          <Text size="sm" tone="subtle" align="center">
                            {emptyMessage}
                          </Text>
                        </div>
                      ) : (
                        filteredOptions.length === 0 && <SearchEmpty term={search.trim()} onClear={() => setSearch("")} />
                      )}
                    </SheetBody>
                  </MotionSurface>
                </Dialog.Content>
              </Dialog.Portal>
            )}
          </AnimatePresence>
        </FieldReset>
      </Dialog.Root>
    </Field>
  );
}
